import React, { useEffect, useState } from 'react';
import { API_HOST } from 'appConstants';
import PropTypes from 'prop-types';
import { useIntl, FormattedMessage } from 'react-intl';
import { showSuccessMessage, showErrorMessage, getErrorText } from 'helpers';
import axios from 'axios';
import fileDownload from 'js-file-download';
import cx from 'classnames';
import './ExportModalFile.sass';

export const ExportModalFile = ({
  onClose,
  templateName,
  templateId,
  isLoading,
  setIsLoading,
  selectedItem,
  setSelectedItem,
}) => {
  const intl = useIntl();

  const [imageExtraOptions, setImageExtraOptions] = useState({
    desktopWidth: 600,
    mobileWidth: 320,
    format: 'png',
  });

  const FILE_NAMES = [
    {
      name: 'pdf',
      ico: 'pdf',
      value: 'PDF',
    },
    {
      name: 'html',
      ico: 'html',
      value: 'HTML',
    },
    {
      name: 'image',
      ico: 'image',
      value: 'Image',
    },
  ];

  const showFileName = (name, array) => {
    const found = array.find((item) => item.name === name);
    return found ? found.value : '';
  };

  const handleDownload = async (url, filename) => {
    try {
      setIsLoading(true);

      await axios
        .get(url, {
          responseType: 'blob',
        })
        .then((res) => {
          fileDownload(res.data, filename);

          showSuccessMessage(
            intl.formatMessage({ id: 'export-modal.success-message' }),
          );

          setIsLoading(false);

          onClose();
        });
    } catch {
      showErrorMessage(getErrorText());
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      setIsLoading(false);
      setSelectedItem('pdf');
    };
  }, [onClose, setIsLoading, setSelectedItem]);

  return (
    <div className="export-modal-file">
      <div className="export-modal__items">
        {FILE_NAMES.map(({ name, ico, value }) => (
          <button
            key={`${name}-file`}
            data-testid={`export-${name}-item`}
            disabled={isLoading}
            onClick={() => setSelectedItem(name)}
            className={cx('export-modal__items__item', {
              loading: isLoading && selectedItem === name,
              active: selectedItem === name,
            })}>
            <img
              src={`https://cdn11.waymore.io/dnd/images/export_icons/${ico}.svg`}
              alt={value}
            />
            <span className="xport-modal-file__items__item__title">
              {value}
            </span>
          </button>
        ))}
      </div>
      <div
        className={cx('export-modal-file__details', {
          active:
            selectedItem &&
            (selectedItem === 'pdf' ||
              selectedItem === 'html' ||
              selectedItem === 'image'),
        })}>
        <h2>
          {intl.formatMessage({ id: 'export-modal.options-title' })}
          {showFileName(selectedItem, FILE_NAMES)}
        </h2>
        {selectedItem === 'pdf' && (
          <div className="export-modal-file__details__actions">
            <button
              data-testid="export-pdf"
              disabled={isLoading}
              onClick={() =>
                handleDownload(
                  `${API_HOST}/api/v2/usertemplate/export/${templateId}/pdf`,
                  `${templateName}.pdf`,
                )
              }>
              <span className="icon icon-cloud-download" />
              <FormattedMessage id="export-modal.image-options.button-actions.pdf-button" />
            </button>
          </div>
        )}
        {selectedItem === 'html' && (
          <div className="export-modal-file__details__actions">
            <button
              data-testid="export-html"
              disabled={isLoading}
              onClick={() =>
                handleDownload(
                  `${API_HOST}/api/v2/usertemplate/export/${templateId}/html`,
                  `${templateName}.html`,
                )
              }>
              <span className="icon icon-cloud-download" />
              <FormattedMessage id="export-modal.image-options.button-actions.html-button" />
            </button>
          </div>
        )}
        {selectedItem === 'image' && (
          <>
            <div className="export-modal-file__details__input-range">
              <label>
                <FormattedMessage id="export-modal.image-options.desktop-range-title" />
                ({imageExtraOptions.desktopWidth})
              </label>
              <input
                type="range"
                onChange={(e) =>
                  setImageExtraOptions({
                    ...imageExtraOptions,
                    desktopWidth: Number(e.target.value),
                  })
                }
                id="desktop-width"
                name="desktop_width"
                min="600"
                max="1920"
                value={imageExtraOptions.desktopWidth || '600'}
              />
            </div>
            <div className="export-modal-file__details__input-range">
              <label>
                <FormattedMessage id="export-modal.image-options.mobile-range-title" />
                ({imageExtraOptions.mobileWidth})
              </label>
              <input
                type="range"
                onChange={(e) =>
                  setImageExtraOptions({
                    ...imageExtraOptions,
                    mobileWidth: Number(e.target.value),
                  })
                }
                id="desktop-width"
                name="desktop_width"
                min="320"
                max="599"
                value={imageExtraOptions.mobileWidth || '320'}
              />
            </div>
            <div className="export-modal-file__details__format-selection">
              <button
                disabled={isLoading}
                onClick={() =>
                  setImageExtraOptions({
                    ...imageExtraOptions,
                    format: 'png',
                  })
                }
                className={cx({ active: imageExtraOptions.format === 'png' })}>
                PNG
              </button>
              <button
                onClick={() =>
                  setImageExtraOptions({
                    ...imageExtraOptions,
                    format: 'jpeg',
                  })
                }
                className={cx({ active: imageExtraOptions.format === 'jpeg' })}>
                JPG
              </button>
            </div>
            <div className="export-modal-file__details__actions">
              <p>
                <FormattedMessage id="export-modal.image-options.button-actions.title" />
              </p>
              <button
                data-testid="export-desktop-image"
                disabled={isLoading}
                onClick={() =>
                  handleDownload(
                    `${API_HOST}/api/v2/usertemplate/export/${templateId}/image?width=${imageExtraOptions.desktopWidth}&format=${imageExtraOptions.format}`,
                    `${templateName}.${imageExtraOptions.format}`,
                  )
                }>
                <span className="icon icon-cloud-download" />
                <FormattedMessage id="export-modal.image-options.button-actions.desktop-button" />
              </button>
              <button
                data-testid="export-mobile-image"
                disabled={isLoading}
                onClick={() =>
                  handleDownload(
                    `${API_HOST}/api/v2/usertemplate/export/${templateId}/image?width=${imageExtraOptions.mobileWidth}&format=${imageExtraOptions.format}`,
                    `${templateName}.${imageExtraOptions.format}`,
                  )
                }>
                <span className="icon icon-cloud-download" />
                <FormattedMessage id="export-modal.image-options.button-actions.mobile-button" />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

ExportModalFile.propTypes = {
  onClose: PropTypes.func.isRequired,
  templateName: PropTypes.string,
  templateId: PropTypes.string,
  isLoading: PropTypes.bool.isRequired,
  setIsLoading: PropTypes.func.isRequired,
  selectedItem: PropTypes.string,
  setSelectedItem: PropTypes.func.isRequired,
};

export default ExportModalFile;
